/**
 * TellMetricsOverlay - Compact overlay showing tell metrics on video cards
 * For use in TellCarousel video previews
 */

import React from 'react';
import { getConfidenceLevel, getConfidenceColor, PITCH_TYPE_LABELS } from '../../lib/wireTypes';

/**
 * TellMetricsOverlay Component
 * @param {Object} props
 * @param {string} props.tellName - Name of the tell (e.g., "Glove Position")
 * @param {number} props.confidence - Confidence score 0-1
 * @param {string[]} props.pitchTypesAffected - Array of pitch type codes
 * @param {string} [props.timestamp] - Optional timestamp marker (e.g., "00:06")
 * @param {string} [props.variant='default'] - 'default' | 'compact' | 'badge'
 */
export function TellMetricsOverlay({
  tellName,
  confidence,
  pitchTypesAffected = [],
  timestamp,
  variant = 'default',
}) {
  const level = getConfidenceLevel(confidence);
  const color = getConfidenceColor(level);
  const confidencePercent = Math.round(confidence * 100);

  if (variant === 'badge') {
    return (
      <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/70 backdrop-blur">
        <div
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: color }}
        />
        <span className="text-xs font-medium text-white truncate max-w-[100px]">
          {tellName}
        </span>
        <span
          className="text-xs font-mono font-bold"
          style={{ color }}
        >
          {confidencePercent}%
        </span>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className="flex items-center gap-2 px-2 py-1 rounded bg-black/60 backdrop-blur">
        <div
          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: color }}
        />
        <span className="text-[10px] text-white/90 truncate">
          {tellName}
        </span>
        <span
          className="text-[10px] font-mono font-bold ml-auto"
          style={{ color }}
        >
          {confidencePercent}%
        </span>
      </div>
    );
  }

  // Default variant - full metrics display
  return (
    <div className="p-2 rounded-lg bg-black/70 backdrop-blur border border-white/10">
      {/* Tell Name + Confidence */}
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5">
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: color }}
          />
          <span className="text-xs font-medium text-white">
            {tellName}
          </span>
        </div>
        <span
          className="text-xs font-mono font-bold"
          style={{ color }}
        >
          {confidencePercent}%
        </span>
      </div>

      {/* Pitch Types Affected */}
      {pitchTypesAffected.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {pitchTypesAffected.map(pt => (
            <span
              key={pt}
              className="px-1.5 py-0.5 bg-white/10 text-white/90 rounded text-[10px] font-mono"
            >
              {pt}
            </span>
          ))}
        </div>
      )}

      {/* Timestamp */}
      {timestamp && (
        <div className="flex items-center gap-1 text-white/60">
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          <span className="text-[10px] font-mono">{timestamp}</span>
        </div>
      )}
    </div>
  );
}

/**
 * TellBadge - Simple inline badge for tell category
 */
export function TellBadge({ category, count, avgConfidence }) {
  const level = getConfidenceLevel(avgConfidence);
  const color = getConfidenceColor(level);

  return (
    <div
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all hover:scale-105 cursor-pointer"
      style={{
        backgroundColor: `${color}20`,
        borderColor: `${color}40`,
        borderWidth: 1,
        color,
      }}
    >
      <span>{category}</span>
      {count && (
        <span className="px-1.5 py-0.5 bg-white/20 rounded-full text-[10px]">
          {count}
        </span>
      )}
    </div>
  );
}

/**
 * TellConfidenceBar - Visual confidence indicator
 */
export function TellConfidenceBar({ confidence, showLabel = true }) {
  const level = getConfidenceLevel(confidence);
  const color = getConfidenceColor(level);
  const percent = Math.round(confidence * 100);

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-zinc-700 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{
            width: `${percent}%`,
            backgroundColor: color,
          }}
        />
      </div>
      {showLabel && (
        <span
          className="text-xs font-mono font-bold min-w-[32px] text-right"
          style={{ color }}
        >
          {percent}%
        </span>
      )}
    </div>
  );
}

export default TellMetricsOverlay;
